(() => {
  'use strict';
  const make = (tag, cls, text) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  const init = root => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const assets = JSON.parse(root.querySelector('[data-neb-review-assets]').textContent);
    const strings = JSON.parse(root.querySelector('[data-neb-review-strings]').textContent);
    const dimensions = JSON.parse(root.querySelector('[data-neb-review-dimensions]').textContent);
    const resolve = photo => assets[photo] || photo;
    const list = root.querySelector('[data-neb-review-list]');
    const status = root.querySelector('[data-neb-review-status]');
    const more = root.querySelector('[data-neb-review-more]');
    const rating = root.querySelector('[data-neb-review-rating]');
    const photosOnly = root.querySelector('[data-neb-review-photos]');
    const sort = root.querySelector('[data-neb-review-sort]');
    const identityFor = review => {
      const name = typeof review.author === 'string' ? review.author.trim() : '';
      if (name && !/^ANON[-_]|^Customer review$/i.test(name)) {
        return { name, initials: name.split(/\s+/).slice(0,2).map(part=>Array.from(part)[0]).join('') };
      }
      const id = String(review.id || '').match(/\d+$/)?.[0];
      return { name: id ? strings.anonymous.replace('__ID__',id) : strings.author, initials: id ? id.slice(-2).padStart(2,'0') : 'AR' };
    };
    let data;
    let pending;
    let shown = 8;
    let revision = 0;
    const getData = async () => {
      if (data) return data;
      if (!pending) pending = fetch(root.dataset.url, { signal: AbortSignal.timeout(15000) }).then(response => {
        if (!response.ok) throw new Error(strings.retry);
        return response.json();
      }).then(result => {
        data = result;
        const rows = new Map(data.map(review=>[review.id,review]));
        list.querySelectorAll('.neb-review-card').forEach(node=>{
          const review = rows.get(node.dataset.reviewId);
          if (!review) return;
          const display = identityFor(review);
          node.querySelector('.neb-review-card__author strong').textContent = display.name;
          node.querySelector('.neb-review-card__author > span').textContent = display.initials;
        });
        return data;
      }).catch(error => { pending = null; throw error; });
      return pending;
    };
    const photoRank = review => review.photos.some(photo=>dimensions[photo]?.width && dimensions[photo]?.height) ? 2 : Number(review.photos.length > 0);
    const filtered = rows => rows.filter(r => (rating.value === 'all' || r.rating === Number(rating.value)) && (!photosOnly.checked || r.photos.length)).sort((a,b) => {
      if (sort.value === 'photos') return photoRank(b) - photoRank(a) || b.date.localeCompare(a.date);
      if (sort.value === 'oldest') return a.date.localeCompare(b.date);
      if (sort.value === 'highest') return b.rating - a.rating || b.date.localeCompare(a.date);
      if (sort.value === 'lowest') return a.rating - b.rating || b.date.localeCompare(a.date);
      return b.date.localeCompare(a.date);
    });
    const card = review => {
      const node = make('article','neb-review-card');
      node.dataset.reviewId = review.id;
      const header = make('header','neb-review-card__header');
      const author = make('div','neb-review-card__author');
      const display = identityFor(review);
      const avatar = make('span','',display.initials);avatar.setAttribute('aria-hidden','true');
      const identity = make('div');
      const stars = make('span','neb-stars','★'.repeat(review.rating) + '☆'.repeat(5-review.rating));stars.setAttribute('aria-label', `${review.rating} / 5`);
      identity.append(make('strong','',display.name),stars);author.append(avatar,identity);
      const displayDate = new Intl.DateTimeFormat(document.documentElement.lang || 'en',{month:'short',day:'numeric',year:'numeric'}).format(new Date(review.date+'T12:00:00'));
      const date = make('time','',displayDate);date.dateTime=review.date;
      header.append(author,date);node.append(header);
      const content = make('div','neb-review-card__content');content.append(make('p','neb-review-card__body',review.body));node.append(content);
      if (review.photos.length) {
        const media = make('div','neb-review-card__media');
        review.photos.forEach((photo,index) => {
          const link = make('a',index ? 'neb-review-card__extra' : '');
          link.href = resolve(photo);link.dataset.nebReviewPhoto = '';link.dataset.reviewId = review.id;link.dataset.photoIndex = index;
          link.setAttribute('aria-label',strings.open);
          if (!dimensions[photo]?.width || !dimensions[photo]?.height) {
            link.classList.add('neb-review-card__external-photo');link.dataset.nebReviewExternal = 'true';
            link.target = '_blank';link.rel = 'noopener noreferrer';link.textContent = strings.external;
            media.append(link);return;
          }
          const img = make('img');img.src = resolve(photo);img.alt = strings.photo;
          if (dimensions[photo]?.width && dimensions[photo]?.height) { img.width = dimensions[photo].width;img.height = dimensions[photo].height; }
          img.loading = 'lazy';img.decoding = 'async';
          img.addEventListener('error',() => { link.hidden = true; });
          link.append(img);media.append(link);
        });
        node.append(media);
      }
      return node;
    };
    let layoutFrame;
    const layout = () => {
      cancelAnimationFrame(layoutFrame);
      layoutFrame = requestAnimationFrame(() => {
        const gap = parseFloat(getComputedStyle(list).columnGap) || 16;
        list.querySelectorAll('.neb-review-card').forEach(node => { node.style.gridRowEnd = 'span ' + Math.ceil((node.getBoundingClientRect().height + gap) / 8); });
        list.classList.add('is-masonry');
      });
    };
    const observer = new ResizeObserver(layout);
    const observeCards = () => { list.querySelectorAll('.neb-review-card').forEach(node => observer.observe(node));layout(); };
    new ResizeObserver(layout).observe(list);
    document.fonts.ready.then(layout);
    observeCards();
    const update = async (reset = false) => {
      const token = ++revision;
      root.setAttribute('aria-busy','true');
      more.disabled = true;
      try {
        const rows = filtered(await getData());
        if (token !== revision) return;
        shown = reset ? 8 : shown + 8;
        const selected = rows.slice(0,shown);
        if (reset) { observer.disconnect();list.replaceChildren(...selected.map(card)); }
        else { list.append(...selected.slice(list.children.length).map(card)); }
        observeCards();
        status.textContent = rows.length ? strings.showing.replace('__SHOWN__',selected.length).replace('__COUNT__',rows.length) : strings.empty;
        more.hidden = selected.length >= rows.length;
      } catch (_) { if (token === revision) status.textContent = strings.retry; }
      finally { if (token === revision) { root.removeAttribute('aria-busy'); more.disabled = false; } }
    };
    more.addEventListener('click',()=>update());
    [rating,photosOnly,sort].forEach(input=>input.addEventListener('change',()=>update(true)));
    root.addEventListener('error',event=>{
      if (event.target.matches('[data-neb-review-photo] img')) event.target.closest('a').hidden = true;
    },true);
    root.addEventListener('click',async event=>{
      const link = event.target.closest('[data-neb-review-photo]');
      if (!link) return;
      if (link.dataset.nebReviewExternal) return;
      event.preventDefault();
      let review;
      try { review = (await getData()).find(r=>r.id===link.dataset.reviewId); }
      catch (_) { location.assign(link.href); return; }
      if (!review) return;
      let index = Number(link.dataset.photoIndex)||0;
      const dialog = make('dialog','neb-review-dialog');
      const bar = make('div','neb-review-dialog__bar');
      const close = make('button','','×');
      close.type='button'; close.setAttribute('aria-label',strings.close);
      bar.append(make('strong','',identityFor(review).name),close);
      const img = make('img');
      img.alt = strings.photo;
      const caption = make('p','neb-review-dialog__caption');
      const paint = ()=>{ img.src = resolve(review.photos[index]); caption.textContent = `${review.rating}/5 · ${index+1}/${review.photos.length}`; };
      dialog.append(bar,img,caption);
      if (review.photos.length>1) {
        const next=make('button','neb-review-dialog__next',strings.next); next.type='button';
        next.addEventListener('click',()=>{ index=(index+1)%review.photos.length; paint(); });
        dialog.append(next);
      }
      document.body.append(dialog);
      close.addEventListener('click',()=>dialog.close());
      dialog.addEventListener('click',event=>{ if (event.target===dialog) { const rect=dialog.getBoundingClientRect(); if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom) dialog.close(); } });
      dialog.addEventListener('close',()=>{dialog.remove();link.focus();},{once:true});
      paint(); dialog.showModal();
    });
  };
  const initAll=node=>node.querySelectorAll('[data-neb-reviews]').forEach(init);
  initAll(document);
  document.addEventListener('shopify:section:load',event=>initAll(event.target));
})();
